import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Edit, Trash2, Dumbbell, Plus, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const ROLE_STYLE = {
    admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
    gym: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
    trainer: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
    standalone: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200',
    user: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
    manager: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
};

const isWorkoutActive = (workout) =>
    workout.active === '1' || workout.active === 1 || workout.active === true;

const formatDateTime = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const formatDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('it-IT');
};

const isTesterUser = (user) =>
    user.is_tester === 1 || user.is_tester === '1' || user.is_tester === true;

const issuesForUser = (user) => {
    const issues = [];
    if (!user.active) issues.push('inactive');
    if (!user.last_login) issues.push('neverLogin');
    if (!user.email) issues.push('emailMissing');
    else if (!user.email_verified_at) issues.push('emailUnverified');
    if (Array.isArray(user.workouts) && user.workouts.length === 0 && user.role_name !== 'admin' && user.role_name !== 'gym') {
        issues.push('noSheets');
    }
    return issues;
};

const matchesCount = (user, key) => {
    if (!key || key === 'total') return true;
    if (key === 'tester') return isTesterUser(user);
    return issuesForUser(user).includes(key);
};

const compareValues = (a, b, dir) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === 'number' && typeof b === 'number') {
        return dir === 'asc' ? a - b : b - a;
    }
    const result = String(a).localeCompare(String(b), 'it', { sensitivity: 'base' });
    return dir === 'asc' ? result : -result;
};

const UserVerifyTable = ({ users, onEdit, onDelete, onDeleteMany, onWorkoutRemoved, onCreateWorkout }) => {
    const { t } = useTranslation();
    const { getAuthenticatedApi, currentUser } = useAuth();
    const api = getAuthenticatedApi();
    const selfId = currentUser?.user_id ?? currentUser?.id;
    const [onlyIssues, setOnlyIssues] = useState(false);
    const [countFilter, setCountFilter] = useState(null);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [deleting, setDeleting] = useState(false);
    const [sortKey, setSortKey] = useState('last_login');
    const [sortDir, setSortDir] = useState('desc');
    const [expandedId, setExpandedId] = useState(null);

    const roleLabel = (role) => {
        const key = `admin.userVerify.roles.${role}`;
        const label = t(key);
        return label === key ? (role || '—') : label;
    };

    const summary = useMemo(() => {
        const counts = {
            total: users.length,
            inactive: 0,
            neverLogin: 0,
            emailMissing: 0,
            emailUnverified: 0,
            noSheets: 0,
            tester: 0
        };
        users.forEach((user) => {
            issuesForUser(user).forEach((issue) => {
                counts[issue] += 1;
            });
            if (isTesterUser(user)) counts.tester += 1;
        });
        return counts;
    }, [users]);

    const rows = useMemo(() => {
        const list = users.filter((user) => {
            if (onlyIssues && issuesForUser(user).length === 0) return false;
            return matchesCount(user, countFilter);
        });
        const valueOf = (user) => {
            const workouts = Array.isArray(user.workouts) ? user.workouts : [];
            switch (sortKey) {
                case 'id':
                    return Number(user.id) || 0;
                case 'user':
                    return user.name || user.username || '';
                case 'role':
                    return user.role_name || '';
                case 'gym':
                    return user.gym_name || '';
                case 'plan':
                    return user.plan?.name || '';
                case 'status':
                    return user.active ? 1 : 0;
                case 'last_login':
                    return user.last_login ? new Date(user.last_login).getTime() : null;
                case 'sheets':
                    return workouts.length;
                default:
                    return '';
            }
        };
        return [...list].sort((a, b) => compareValues(valueOf(a), valueOf(b), sortDir));
    }, [users, onlyIssues, countFilter, sortKey, sortDir]);

    const isSelf = (user) => selfId != null && String(user.id) === String(selfId);
    const selectableRows = rows.filter((user) => !isSelf(user));
    const selectedRows = selectableRows.filter((user) => selectedIds.has(String(user.id)));
    const allVisibleSelected = selectableRows.length > 0 && selectedRows.length === selectableRows.length;

    const applyCountFilter = (key) => {
        setCountFilter((prev) => (prev === key || key === 'total' ? null : key));
        setSelectedIds(new Set());
    };

    const toggleRow = (user) => {
        if (isSelf(user)) return;
        const id = String(user.id);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAllVisible = () => {
        setSelectedIds((prev) => {
            if (allVisibleSelected) return new Set();
            const next = new Set(prev);
            selectableRows.forEach((user) => next.add(String(user.id)));
            return next;
        });
    };

    const handleDeleteSelected = async () => {
        if (!selectedRows.length || deleting || !onDeleteMany) return;
        const confirmed = window.confirm(t('admin.userVerify.confirmDeleteMany', { count: selectedRows.length }));
        if (!confirmed) return;
        setDeleting(true);
        try {
            const failed = await onDeleteMany(selectedRows);
            setSelectedIds(new Set());
            if (failed?.length) {
                window.alert(t('admin.userVerify.deleteManyError', { names: failed.join(', ') }));
            }
        } finally {
            setDeleting(false);
        }
    };

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
            return;
        }
        setSortKey(key);
        setSortDir(key === 'user' || key === 'role' || key === 'gym' || key === 'plan' ? 'asc' : 'desc');
    };

    const handleRemoveWorkout = async (user, workout) => {
        const schedaId = workout.scheda_id;
        if (!schedaId) return;
        if (!window.confirm(t('admin.userVerify.confirmDeleteSheet'))) return;
        try {
            await api.delete(`/schede.php?id=${schedaId}`);
            onWorkoutRemoved?.(user.id, schedaId);
        } catch (err) {
            console.error('Error deleting workout:', err);
            alert(t('admin.userVerify.deleteSheetError'));
        }
    };

    const headerButton = (key, label) => (
        <button
            type="button"
            onClick={() => toggleSort(key)}
            className="inline-flex items-center gap-1 text-left font-semibold uppercase tracking-wide hover:text-gray-900 dark:hover:text-white"
        >
            {label}
            {sortKey === key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
        </button>
    );

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
                {[
                    ['total', summary.total],
                    ['inactive', summary.inactive],
                    ['neverLogin', summary.neverLogin],
                    ['emailMissing', summary.emailMissing],
                    ['emailUnverified', summary.emailUnverified],
                    ['noSheets', summary.noSheets],
                    ['tester', summary.tester]
                ].map(([key, value]) => {
                    const active = key === 'total' ? !countFilter : countFilter === key;
                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => applyCountFilter(key)}
                            className={`rounded-lg border px-3 py-2 text-left transition ${
                                active
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 ring-2 ring-blue-500'
                                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 hover:border-blue-300'
                            }`}
                        >
                            <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                {t(`admin.userVerify.summary.${key}`)}
                            </p>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">{value}</p>
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                    type="checkbox"
                    checked={onlyIssues}
                    onChange={(e) => setOnlyIssues(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                {t('admin.userVerify.onlyIssues')}
            </label>
            <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                    {t('admin.userVerify.selectedCount', { count: selectedRows.length })}
                </span>
                <button
                    type="button"
                    onClick={handleDeleteSelected}
                    disabled={!selectedRows.length || deleting}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <Trash2 className="h-4 w-4" />
                    {deleting ? t('admin.userVerify.deleting') : t('admin.userVerify.deleteSelected')}
                </button>
            </div>
            </div>

            {rows.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
                    {t('admin.userVerify.empty')}
                </p>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="min-w-[1100px] w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-500 dark:text-gray-400 text-xs">
                            <tr>
                                <th className="w-8 px-2 py-2">
                                    <input
                                        type="checkbox"
                                        checked={allVisibleSelected}
                                        onChange={toggleAllVisible}
                                        disabled={!selectableRows.length}
                                        aria-label={t('admin.userVerify.selectAll')}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                </th>
                                <th className="w-8 px-2 py-2" />
                                <th className="px-2 py-2 text-left">{headerButton('id', t('admin.userVerify.colId'))}</th>
                                <th className="px-2 py-2 text-left">{headerButton('user', t('admin.userVerify.colUser'))}</th>
                                <th className="px-2 py-2 text-left">{headerButton('role', t('admin.userVerify.colRole'))}</th>
                                <th className="px-2 py-2 text-left">{headerButton('gym', t('admin.userVerify.colGym'))}</th>
                                <th className="px-2 py-2 text-left">{headerButton('plan', t('admin.userVerify.colPlan'))}</th>
                                <th className="px-2 py-2 text-left">{headerButton('status', t('admin.userVerify.colStatus'))}</th>
                                <th className="px-2 py-2 text-left">{headerButton('last_login', t('admin.userVerify.colLastLogin'))}</th>
                                <th className="px-2 py-2 text-left">{headerButton('sheets', t('admin.userVerify.colSheets'))}</th>
                                <th className="px-2 py-2 text-right">{t('admin.userVerify.colActions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {rows.map((user) => {
                                const workouts = Array.isArray(user.workouts) ? user.workouts : [];
                                const activeSheets = workouts.filter(isWorkoutActive).length;
                                const issues = issuesForUser(user);
                                const expanded = expandedId === user.id;
                                const isTester = isTesterUser(user);
                                const checked = selectedIds.has(String(user.id));
                                return (
                                    <React.Fragment key={user.id}>
                                        <tr className={`align-top ${checked ? 'bg-blue-50 dark:bg-blue-950/30' : 'bg-white dark:bg-gray-800'}`}>
                                            <td className="px-2 py-2">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={isSelf(user)}
                                                    onChange={() => toggleRow(user)}
                                                    aria-label={user.username}
                                                    title={isSelf(user) ? t('admin.userVerify.cannotSelectSelf') : undefined}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40"
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandedId(expanded ? null : user.id)}
                                                    className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                                                    title={t('admin.userVerify.expand')}
                                                >
                                                    {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                                </button>
                                            </td>
                                            <td className="px-2 py-2 font-mono text-xs text-gray-500">#{user.id}</td>
                                            <td className="px-2 py-2">
                                                <div className="font-medium text-gray-900 dark:text-white">
                                                    {user.name || user.username}
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">@{user.username}</div>
                                                <div className={`text-xs ${user.email ? 'text-gray-600 dark:text-gray-300' : 'text-red-600 dark:text-red-400'}`}>
                                                    {user.email || t('admin.userVerify.emailMissing')}
                                                </div>
                                                {user.phone ? (
                                                    <div className="text-xs text-gray-500">{user.phone}</div>
                                                ) : null}
                                            </td>
                                            <td className="px-2 py-2">
                                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_STYLE[user.role_name] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}>
                                                    {roleLabel(user.role_name)}
                                                </span>
                                                {isTester ? (
                                                    <div className="mt-1 text-[11px] uppercase tracking-wide text-amber-700 dark:text-amber-300">
                                                        {t('admin.userVerify.tester')}
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td className="px-2 py-2 text-gray-800 dark:text-gray-200">
                                                {user.gym_name || <span className="text-gray-400">{t('admin.userVerify.noGym')}</span>}
                                                {user.trainer?.name || user.trainer?.username ? (
                                                    <div className="text-xs text-gray-500">
                                                        {t('admin.userVerify.trainer')}: {user.trainer.name || user.trainer.username}
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td className="px-2 py-2 text-gray-800 dark:text-gray-200">
                                                {user.plan?.name || <span className="text-gray-400">{t('admin.userVerify.noPlan')}</span>}
                                            </td>
                                            <td className="px-2 py-2">
                                                <div className="flex flex-col gap-1">
                                                    <span className={`inline-flex w-fit px-2 py-0.5 rounded-full text-xs ${user.active ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}>
                                                        {user.active ? t('admin.userVerify.active') : t('admin.userVerify.inactive')}
                                                    </span>
                                                    {user.email && user.email_verified_at ? (
                                                        <span className="text-[11px] text-emerald-700 dark:text-emerald-300">{t('admin.userVerify.verified')}</span>
                                                    ) : null}
                                                    {issues.filter((issue) => issue !== 'inactive').map((issue) => (
                                                        <span key={issue} className="text-[11px] text-amber-700 dark:text-amber-300">
                                                            {t(`admin.userVerify.flags.${issue}`)}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="px-2 py-2 text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                                {formatDateTime(user.last_login) || t('admin.userVerify.flags.neverLogin')}
                                                <div className="text-gray-400">
                                                    {t('admin.userVerify.createdAt')} {formatDate(user.created_at) || '—'}
                                                </div>
                                            </td>
                                            <td className="px-2 py-2 whitespace-nowrap">
                                                <span className="font-medium text-gray-900 dark:text-white">{workouts.length}</span>
                                                <span className="text-xs text-gray-500"> · {activeSheets} {t('admin.userVerify.sheetsActive')}</span>
                                            </td>
                                            <td className="px-2 py-2">
                                                <div className="flex justify-end gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => onCreateWorkout(user)}
                                                        className="p-1.5 rounded-md text-indigo-700 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
                                                        title={t('admin.userCard.createWorkout')}
                                                    >
                                                        <Plus className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => onEdit(user)}
                                                        className="p-1.5 rounded-md text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-900/30"
                                                        title={t('admin.userCard.edit')}
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => onDelete(user)}
                                                        className="p-1.5 rounded-md text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/30"
                                                        title={t('admin.userCard.delete')}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {expanded && (
                                            <tr className="bg-gray-50 dark:bg-gray-900/40">
                                                <td colSpan={11} className="px-4 py-3">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                            {t('admin.userCard.assignedWorkouts')}
                                                        </p>
                                                        <button
                                                            type="button"
                                                            onClick={() => onCreateWorkout(user)}
                                                            className="inline-flex items-center gap-1 text-xs text-indigo-700 dark:text-indigo-300"
                                                        >
                                                            <Plus className="h-3.5 w-3.5" />
                                                            {t('admin.userCard.createWorkout')}
                                                        </button>
                                                    </div>
                                                    {workouts.length === 0 ? (
                                                        <p className="text-sm text-gray-500">{t('admin.userCard.noWorkouts')}</p>
                                                    ) : (
                                                        <ul className="space-y-1">
                                                            {workouts.map((workout) => (
                                                                <li
                                                                    key={workout.id || workout.scheda_id}
                                                                    className="flex items-center justify-between gap-3 rounded-md bg-white dark:bg-gray-800 px-2 py-1.5"
                                                                >
                                                                    <div className="flex items-center gap-2 min-w-0">
                                                                        <Dumbbell className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                                                        <span className="truncate text-gray-800 dark:text-gray-200">{workout.scheda_nome}</span>
                                                                        <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${isWorkoutActive(workout) ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'}`}>
                                                                            {isWorkoutActive(workout) ? t('admin.userCard.active') : t('admin.userCard.inactiveStatus')}
                                                                        </span>
                                                                        <span className="text-xs text-gray-400">
                                                                            {formatDate(workout.data_creazione)}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center gap-1 flex-shrink-0">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => onCreateWorkout(user, 'edit', workout)}
                                                                            className="p-1 text-blue-600 dark:text-blue-400"
                                                                            title={t('admin.userCard.editWorkout')}
                                                                        >
                                                                            <Edit className="h-4 w-4" />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleRemoveWorkout(user, workout)}
                                                                            className="p-1 text-red-600 dark:text-red-400"
                                                                            title={t('admin.userCard.removeWorkout')}
                                                                        >
                                                                            <X className="h-4 w-4" />
                                                                        </button>
                                                                    </div>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default UserVerifyTable;
